/**
 * retentionService - API calls do módulo de Retenção de e-mails (Plano B, Task 8)
 *
 * Fila de decisão de e-mails inbound antes de virarem ticket (`GET/POST
 * /api/email-retentions*`, backend Task 4/5). Sem `create`/`update`/`delete`
 * genéricos — entries são criadas só internamente pelo webhook; transições de
 * status são só `approve`/`block`.
 */

import type { AxiosRequestConfig } from 'axios';
import type {
  ContactResponse,
  EmailRetentionListResponse,
  EmailRetentionQuery,
  EmailRetentionResponse,
  EmailRetentionStatus,
} from '@troia-v3/types';

import { api } from '@/lib/api';

/**
 * Suprime o toast genérico do interceptor global (`src/lib/api.ts`) — os
 * callers (`RetentionReadingPane`) tratam erro localmente: toast custom com a
 * mensagem real do backend, e tratamento especial pro 409 (entry já revisada
 * por outro admin). Mesmo padrão de `ticketsService.HANDLE_ERROR_LOCALLY`.
 */
const HANDLE_ERROR_LOCALLY: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
  _handleErrorLocally: true,
};

export const retentionService = {
  /**
   * GET /email-retentions/count?status= — badge de pendentes na FilterBar de
   * tickets. Fetch SILENCIOSO (`_handleErrorLocally`, sem toast em lugar
   * nenhum): é um polling de 60s — sem o flag, um 5xx persistente toastaria
   * a cada ciclo via interceptor global (fix pós-review Task 8). Falha só
   * deixa o badge ausente; React Query re-tenta no próximo intervalo.
   */
  async getCount(status?: EmailRetentionStatus): Promise<number> {
    const response = await api.get('email-retentions/count', {
      ...HANDLE_ERROR_LOCALLY,
      params: status ? { status } : undefined,
    });
    return response.data.data.count;
  },

  /** GET /email-retentions — lista denormalizada (sem `email.html`). */
  async getAll(query: EmailRetentionQuery): Promise<EmailRetentionListResponse> {
    const response = await api.get('email-retentions', { params: query });
    return response.data.data;
  },

  /** GET /email-retentions/:id — entry completa (com `email.html`/anexos). */
  async getById(id: string): Promise<EmailRetentionResponse> {
    const response = await api.get(`email-retentions/${id}`);
    return response.data.data;
  },

  /**
   * POST /email-retentions/:id/approve — cria o ticket. `trustContact: false`
   * (Aprovar) = só desta vez; `true` (Confiar) = autoriza o contato pra sempre.
   * `customerId` opcional associa o contato a um cliente antes de criar o ticket.
   */
  async approve(id: string, opts?: { trustContact?: boolean; customerId?: string | null }): Promise<EmailRetentionResponse> {
    const body: { trust: boolean; customerId?: string } = { trust: opts?.trustContact ?? false };
    if (opts?.customerId) body.customerId = opts.customerId;
    const response = await api.post(`email-retentions/${id}/approve`, body, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /** POST /email-retentions/:id/discard — descarta a entry sem criar ticket nem tocar o contato. */
  async discard(id: string): Promise<EmailRetentionResponse> {
    const response = await api.post(`email-retentions/${id}/discard`, undefined, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /** POST /email-retentions/:id/block — bloqueia o contato + pendentes em lote. */
  async block(id: string): Promise<EmailRetentionResponse> {
    const response = await api.post(`email-retentions/${id}/block`, undefined, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Info do contato dono do e-mail retido — rota cross-module
   * `GET /contacts/dropdown/:id` (sem `permissionMiddleware` de `contacts`,
   * padrão cross-module do CLAUDE.md), retorna o `ContactResponse` completo.
   */
  async getContact(contactId: string): Promise<ContactResponse> {
    const response = await api.get(`contacts/dropdown/${contactId}`);
    return response.data.data;
  },
};
